// ----------------------------------------------------------------------

const ROOTS = {
  TENANT: '/',
  USER_AUTH: '/auth',
  AUTH: '/dashboard/auth',
  DASHBOARD: '/dashboard',
  OVERVIEW: '/dashboard/overview',
  PROFILE: '/dashboard/profile',
  LOCATIONS: '/dashboard/locations',
  SPACES: '/dashboard/spaces',
  BOOKINGS: '/dashboard/bookings',
  TRANSACTIONS: '/dashboard/transactions',
  EVENTS: '/dashboard/events',
  CLASSES: '/dashboard/classes',
  ADMINS: '/dashboard/admins',
  ROLES: '/dashboard/roles',
  SETTINGS: '/dashboard/settings',
  CUSTOMERS: '/dashboard/customers',
  TENANT_NOT_FOUND: '/tenant-not-found',
  BANNERS: '/dashboard/banners',
  CAMPAIGNS: '/dashboard/campaigns',
  TOURNAMENTS: '/dashboard/tournaments',
  TRIPS: '/dashboard/trips',
  ADDONS: '/dashboard/addons',
  CUSTOMER_GROUPS: '/dashboard/customer-groups',
};

// ----------------------------------------------------------------------

export const paths = {
  // TENANT DETAILS
  tenant: {
    root: ROOTS.TENANT,
    tenantLocation: {
      root: `${ROOTS.TENANT}locations`,
      details: (id: number) => `${ROOTS.TENANT}locations/${id}`,
    },
    experiences: {
      details: (id: number) => `${ROOTS.TENANT}experiences/${id}`,
    },
    booking: {
      success: `${ROOTS.TENANT}booking/success`,
    },
    classes: {
      session: {
        details: (id: number) => `${ROOTS.TENANT}classes/sessions/${id}`,
      },
    },
    trips: {
      details: (id: number) => `${ROOTS.TENANT}trips/${id}`,
    },
  },
  // USER AUTH
  userAuth: {
    signIn: `${ROOTS.USER_AUTH}/sign-in`,
    verifyOtp: `${ROOTS.USER_AUTH}/verify-otp`,
    completeProfile: `${ROOTS.USER_AUTH}/complete-profile`,
  },
  // AUTH
  auth: {
    signIn: `${ROOTS.AUTH}/sign-in`,
    forgetPassword: `${ROOTS.AUTH}/forget-password`,
    resetPassword: `${ROOTS.AUTH}/reset-password`,
  },
  // DASHBOARD
  dashboard: {
    root: ROOTS.LOCATIONS,
  },
  // PROFILE
  profile: {
    root: ROOTS.PROFILE,
  },
  // LOCATIONS
  locations: {
    root: ROOTS.LOCATIONS,
    new: `${ROOTS.LOCATIONS}/new`,
    edit: (id: number) => `${ROOTS.LOCATIONS}/${id}/edit`,
    details: (id: number) => `${ROOTS.LOCATIONS}/${id}`,
    spaces: {
      new: (id: string) => `${ROOTS.LOCATIONS}/${id}/spaces/new`,
      view: (id: string, spaceId: string) =>
        `${ROOTS.LOCATIONS}/${id}/spaces/${spaceId}`,
    },
  },
  banners: {
    root: ROOTS.BANNERS,
    new: `${ROOTS.BANNERS}/new`,
    edit: (id: number) => `${ROOTS.BANNERS}/${id}/edit`,
  },
  addons: {
    root: ROOTS.ADDONS,
    new: `${ROOTS.ADDONS}/new`,
    edit: (id: number) => `${ROOTS.ADDONS}/${id}/edit`,
  },
  spaces: {
    root: ROOTS.SPACES,
    new: `${ROOTS.SPACES}/new`,
    edit: (id: number) => `${ROOTS.SPACES}/${id}/edit`,
    details: (id: number) => `${ROOTS.SPACES}/${id}`,
  },
  // BOOKINGS
  bookings: {
    root: `${ROOTS.BOOKINGS}/list`,
    calendar: `${ROOTS.BOOKINGS}/calendar`,
    details: (id: number) => `${ROOTS.BOOKINGS}/${id}`,
  },
  // TRANSACTIONS
  transactions: {
    root: `${ROOTS.TRANSACTIONS}`,
  },
  // EVENTS
  events: {
    root: ROOTS.EVENTS,
    details: (id: number) => `${ROOTS.EVENTS}/${id}`,
    new: `${ROOTS.EVENTS}/new`,
  },
  // CLASSES
  classes: {
    root: `${ROOTS.CLASSES}/calendar`,
    list: `${ROOTS.CLASSES}/list`,
    details: (classId: number, sessionId: number) =>
      `${ROOTS.CLASSES}/${classId}/sessions/${sessionId}`,
    edit: (classId: number) => `${ROOTS.CLASSES}/${classId}/edit`,
    duplicate: (classId: number) => `${ROOTS.CLASSES}/${classId}/duplicate`,
    editSession: (classId: number, sessionId: number) =>
      `${ROOTS.CLASSES}/${classId}/sessions/${sessionId}/edit`,
    new: `${ROOTS.CLASSES}/new`,
  },
  // CUSTOMERS
  customers: {
    root: `${ROOTS.CUSTOMERS}`,
    view: (id: number) => `${ROOTS.CUSTOMERS}/${id}/view`,
  },
  // CAMPAIGNS
  campaigns: {
    root: ROOTS.CAMPAIGNS,
    new: `${ROOTS.CAMPAIGNS}/new`,
  },
  // ADMINS
  admins: {
    root: ROOTS.ADMINS,
    new: `${ROOTS.ADMINS}/new`,
    edit: (id: number) => `${ROOTS.ADMINS}/${id}/edit`,
  },
  // ROLES
  roles: {
    root: ROOTS.ROLES,
    new: `${ROOTS.ROLES}/new`,
    edit: (id: number) => `${ROOTS.ROLES}/${id}/edit`,
  },
  // SETTINGS
  settings: {
    root: ROOTS.SETTINGS,
    gallery: `${ROOTS.SETTINGS}/gallery`,
    contacts: `${ROOTS.SETTINGS}/contacts`,
  },
  tenantNotFound: {
    root: ROOTS.TENANT_NOT_FOUND,
  },
  tournaments: {
    root: ROOTS.TOURNAMENTS,
    list: `${ROOTS.TOURNAMENTS}/list`,
    details: (id: number) => `${ROOTS.TOURNAMENTS}/${id}`,
    edit: (id: number) => `${ROOTS.TOURNAMENTS}/${id}/edit`,
    new: `${ROOTS.TOURNAMENTS}/new`,
    // Tournament Contact Numbers
    contactNumbers: {
      list: (tournamentId: number) =>
        `${ROOTS.TOURNAMENTS}/${tournamentId}/contact-numbers`,
      new: (tournamentId: number) =>
        `${ROOTS.TOURNAMENTS}/${tournamentId}/contact-numbers/new`,
      edit: (tournamentId: number, contactNumberId: number) =>
        `${ROOTS.TOURNAMENTS}/${tournamentId}/contact-numbers/${contactNumberId}/edit`,
    },
    // Tournament Divisions
    divisions: {
      list: (tournamentId: number) =>
        `${ROOTS.TOURNAMENTS}/${tournamentId}/divisions`,
      new: (tournamentId: number) =>
        `${ROOTS.TOURNAMENTS}/${tournamentId}/divisions/new`,
      details: (tournamentId: string | number, divisionId: string | number) =>
        `${ROOTS.TOURNAMENTS}/${tournamentId}/divisions/${divisionId}/overview`,
      edit: (tournamentId: number, divisionId: number) =>
        `${ROOTS.TOURNAMENTS}/${tournamentId}/divisions/${divisionId}/edit`,
      // Tournament Teams
      teams: {
        list: (tournamentId: number | string, divisionId: number | string) =>
          `${ROOTS.TOURNAMENTS}/${tournamentId}/divisions/${divisionId}/teams`,
        details: (tournamentId: number, divisionId: number, teamId: number) =>
          `${ROOTS.TOURNAMENTS}/${tournamentId}/divisions/${divisionId}/teams/${teamId}`,
        review: (tournamentId: number, divisionId: number, teamId: number) =>
          `${ROOTS.TOURNAMENTS}/${tournamentId}/divisions/${divisionId}/teams/${teamId}/review`,
      },
      // Tournament Division Groups
      groups: {
        list: (tournamentId: number | string, divisionId: number | string) =>
          `${ROOTS.TOURNAMENTS}/${tournamentId}/divisions/${divisionId}/groups`,
      },
      // Tournament Division Matches
      matches: {
        list: (tournamentId: number | string, divisionId: number | string) =>
          `${ROOTS.TOURNAMENTS}/${tournamentId}/divisions/${divisionId}/matches`,
      },
    },

    // Tournament Publishing
    publish: (tournamentId: number) =>
      `${ROOTS.TOURNAMENTS}/${tournamentId}/publish`,
  },
  overview: {
    root: ROOTS.OVERVIEW,
    booking: {
      root: `${ROOTS.OVERVIEW}/booking`,
    },
    userStatistics: {
      root: `${ROOTS.OVERVIEW}/user-statistics`,
    },
    finance: {
      root: `${ROOTS.OVERVIEW}/finance`,
    },
    location: {
      root: `${ROOTS.OVERVIEW}/location`,
    },
    customer: {
      root: `${ROOTS.OVERVIEW}/customer`,
    },
  },
  trips: {
    root: ROOTS.TRIPS,
    list: `${ROOTS.TRIPS}`,
    new: `${ROOTS.TRIPS}/new`,
    edit: (id: number) => `${ROOTS.TRIPS}/${id}/edit`,
    details: (id: number) => `${ROOTS.TRIPS}/${id}`,
  },
  // CUSTOMER GROUPS
  customerGroups: {
    root: ROOTS.CUSTOMER_GROUPS,
    new: `${ROOTS.CUSTOMER_GROUPS}/new`,
    details: (id: number) => `${ROOTS.CUSTOMER_GROUPS}/${id}`,
    edit: (id: number) => `${ROOTS.CUSTOMER_GROUPS}/${id}/edit`,
  },
};
